import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

// Les libellés sont traduits au rendu via t(`auth.${labelKey}`)
const PASSWORD_RULES = [
  { key: 'length',  labelKey: 'pwdRuleLength',  test: (p) => p.length >= 8 },
  { key: 'upper',   labelKey: 'pwdRuleUpper',   test: (p) => /[A-Z]/.test(p) },
  { key: 'lower',   labelKey: 'pwdRuleLower',   test: (p) => /[a-z]/.test(p) },
  { key: 'number',  labelKey: 'pwdRuleNumber',  test: (p) => /[0-9]/.test(p) },
  { key: 'special', labelKey: 'pwdRuleSpecial', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const EyeIcon = ({ open }) => open ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const Register = () => {
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Le code a le bon format mais a été refusé par le serveur (parrain inexistant)
  const [referralRejected, setReferralRejected] = useState(false);

  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const passwordChecks = PASSWORD_RULES.map(r => ({ ...r, pass: r.test(form.password) }));
  const passwordValid   = passwordChecks.every(r => r.pass);
  const passedCount      = passwordChecks.filter(r => r.pass).length;
  const strengthPercent  = form.password.length === 0 ? 0 : Math.max(12, (passedCount / PASSWORD_RULES.length) * 100);
  const strengthLevel    = passedCount <= 2 ? 'weak' : passedCount <= 4 ? 'medium' : 'strong';
  const passwordsMatch  = form.password === form.confirmPassword && form.confirmPassword.length > 0;
  const usernameValid   = form.username.trim().length >= 3;
  const emailValid      = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const referralValid   = /^[A-Z0-9]{8}$/.test(form.referralCode);
  // Le champ est en erreur si le format est mauvais OU si le serveur a refusé le code
  const referralError   = referralRejected || (touched.referralCode && !referralValid);
  const canSubmit       = usernameValid && emailValid && passwordValid && passwordsMatch && referralValid && !loading;

  const handleChange = (field) => (e) => {
    const val = field === 'referralCode'
      ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
      : e.target.value;
    // Dès qu'on modifie le code, on efface le refus serveur (nouvelle tentative)
    if (field === 'referralCode') setReferralRejected(false);
    setForm(f => ({ ...f, [field]: val }));
  };

  const handleBlur = (field) => () => setTouched(t => ({ ...t, [field]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setTouched({ username: true, email: true, password: true, confirmPassword: true, referralCode: true });
    if (!canSubmit) return;

    setLoading(true);
    const result = await register(
      form.username.trim(),
      form.email.trim(),
      form.password,
      form.referralCode
    );
    if (result.success) {
      navigate('/verify-email', { state: { email: result.email } });
    } else {
      // Code de parrainage refusé par le serveur → on le signale SUR le champ
      // (pas de bandeau générique en haut, pour éviter l'incohérence).
      if (/parrainage invalide/i.test(result.error || '')) {
        setReferralRejected(true);
        setTouched(t => ({ ...t, referralCode: true }));
      } else {
        setError(result.error);
      }
      setLoading(false);
    }
  };

  const showPwdRules = touched.password || form.password.length > 0;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>📈 CryptoBot</h1>
          <p>{t('auth.registerTitle')}</p>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>

          {/* ── Nom d'utilisateur ── */}
          <div className={`form-group ${touched.username && !usernameValid ? 'has-error' : ''}`}>
            <label htmlFor="username">{t('auth.username')}</label>
            <input
              type="text"
              id="username"
              value={form.username}
              onChange={handleChange('username')}
              onBlur={handleBlur('username')}
              placeholder="johndoe"
              autoComplete="username"
            />
            {touched.username && !usernameValid && (
              <span className="field-error">{t('auth.usernameMinError')}</span>
            )}
          </div>

          {/* ── Email ── */}
          <div className={`form-group ${touched.email && !emailValid ? 'has-error' : ''}`}>
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              value={form.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              placeholder="your@email.com"
              autoComplete="email"
            />
            {touched.email && !emailValid && (
              <span className="field-error">{t('auth.emailInvalid')}</span>
            )}
          </div>

          {/* ── Mot de passe ── */}
          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={form.password}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
                placeholder="••••••••"
                autoComplete="new-password"
                className={touched.password && !passwordValid ? 'input-error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Masquer' : 'Afficher'}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>

            {showPwdRules && (
              <>
                <div className="password-strength">
                  <div className="password-strength-track">
                    <div
                      className={`password-strength-fill ${strengthLevel}`}
                      style={{ width: `${strengthPercent}%` }}
                    />
                  </div>
                  <span className={`password-strength-label ${strengthLevel}`}>
                    {t(`auth.strength${strengthLevel.charAt(0).toUpperCase()}${strengthLevel.slice(1)}`)}
                  </span>
                </div>

                <ul className="password-requirements">
                  {passwordChecks.map(r => (
                    <li key={r.key} className={`req-item ${r.pass ? 'pass' : 'fail'}`}>
                      <span className="req-icon-wrap">
                        {r.pass ? (
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : (
                          <span className="req-dot" />
                        )}
                      </span>
                      {t(`auth.${r.labelKey}`)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* ── Confirmer le mot de passe ── */}
          <div className={`form-group ${touched.confirmPassword && form.confirmPassword && !passwordsMatch ? 'has-error' : ''}`}>
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirm ? 'text' : 'password'}
                id="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
                placeholder="••••••••"
                autoComplete="new-password"
                className={touched.confirmPassword && form.confirmPassword && !passwordsMatch ? 'input-error' : ''}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirm(v => !v)}
                tabIndex={-1}
                aria-label={showConfirm ? 'Masquer' : 'Afficher'}
              >
                <EyeIcon open={showConfirm} />
              </button>
            </div>
            {touched.confirmPassword && form.confirmPassword && !passwordsMatch && (
              <span className="field-error">{t('auth.passwordMismatch')}</span>
            )}
            {form.confirmPassword.length > 0 && passwordsMatch && (
              <span className="field-success">{t('auth.passwordsMatchOk')}</span>
            )}
          </div>

          {/* ── Code de parrainage (obligatoire) ── */}
          <div className={`form-group referral-group ${referralError ? 'has-error' : ''}`}>
            <label htmlFor="referralCode">
              {t('auth.referralCode')}
            </label>
            <input
              type="text"
              id="referralCode"
              value={form.referralCode}
              onChange={handleChange('referralCode')}
              onBlur={handleBlur('referralCode')}
              placeholder="Ex: A1B2C3D4"
              maxLength={8}
              className={`referral-input ${referralError ? 'input-error' : ''}`}
            />
            {referralRejected ? (
              <span className="field-error">{t('auth.referralInvalid')}</span>
            ) : form.referralCode.length === 0 ? (
              touched.referralCode && <span className="field-error">{t('auth.referralRequired')}</span>
            ) : (
              <span className="referral-hint">
                {form.referralCode.length === 8
                  ? `✓ ${t('auth.referralValid')}`
                  : t('auth.referralCounter').replace('{n}', form.referralCode.length)}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={!canSubmit}
          >
            {loading ? t('auth.creating') : t('auth.register')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {t('auth.hasAccount')}{' '}
            <Link to="/login">{t('auth.login')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
